import { useCallback, useRef, useState } from "react";
import { synthesizeSpeech, isAudioPlaybackAllowed } from "../../api";

// One consistent voice for the whole lesson (gender inferred from the name).
const LESSON_SPEAKER = "المعلم";

/**
 * useLessonAudio — TTS playback for grammar-lesson activities.
 *
 * Unlike useTTS (scenario turns), this caches synthesized audio per text so
 * re-tapping pronoun cards never re-bills TTS, and degrades silently when the
 * caller has no audio seat.
 *
 * Returns:
 *   audioAllowed   — whether play buttons should render at all
 *   playingText    — text currently playing (null if idle)
 *   play(text)     — synthesize (cached) and play; no-ops when not allowed.
 *                    Resolves true when audio actually played, false otherwise
 *                    (lets audio-led activities fall back to showing text).
 *   stop()
 */
export function useLessonAudio() {
  const [playingText, setPlayingText] = useState(null);
  const cacheRef = useRef({}); // text -> base64 mp3
  const audioRef = useRef(null);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingText(null);
  }, []);

  const play = useCallback(
    async (text) => {
      if (!text || !isAudioPlaybackAllowed()) return false;
      stop();
      try {
        if (!cacheRef.current[text]) {
          setPlayingText(text); // show feedback while fetching
          const data = await synthesizeSpeech(text, LESSON_SPEAKER);
          cacheRef.current[text] = data.audio_content;
        }
        const audio = new Audio(`data:audio/mp3;base64,${cacheRef.current[text]}`);
        audioRef.current = audio;
        setPlayingText(text);
        await new Promise((resolve) => {
          audio.onended = resolve;
          audio.onerror = resolve;
          audio.play().catch(resolve);
        });
        return true;
      } catch {
        return false; // audio is a bonus — never block the lesson on it
      } finally {
        setPlayingText(null);
      }
    },
    [stop]
  );

  return { audioAllowed: isAudioPlaybackAllowed(), playingText, play, stop };
}
